import React, { useState, useEffect } from "react";
import DataGrid, { DataGridItem } from "components/data-grid";
import { injectIntl, FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import Card from "components/card";
import { PageSection, PageHeaderBar } from "components/page";
import { ActionWithModalControlled } from "components/buttons";
import withStyles from "components/with-styles";
import styles from "./styles/sport-form.css.js";
import { getScoreboardFormPreviewUrl } from "config/paths";
import { useAuthDataContext } from "components/auth";
import Dialog from "components/dialog";
import { useLoadingContext } from "components/loading-spinner-overlay";
import {
  SCOREBOARD_TYPE_AUTOMATIC,
  SCOREBOARD_TYPE_MANUAL,
  SCOREBOARD_TYPE_NO_SCOREBOARD,
  TEAM_1,
  TEAM_2,
} from "./sport-form-constants";

import ScoreboardTypeApiSelect from "../../../../components/select/scoreboard-type-api-select";
import Button from "@material-ui/core/Button";
import { getScoreboardTemplateThumbnail } from "../../../../config/paths";
import TextDisplay from "../../../../components/display-data/text/text";
import TextField from "@material-ui/core/TextField";
import LogoAssetApiForm from "./sport-form-partial/logo-asset-api-form";
import MatchPointConfigApiForm from "./match-point-config-api-form";
import ScoreboardTemplatesApiTable from "./sport-form-partial/scoreboard-templates-api-table";

const isManualScoreboardType = sportEventData =>
  sportEventData && sportEventData.scoreboardType && sportEventData.scoreboardType.includes(SCOREBOARD_TYPE_MANUAL);

const isAutomaticScoreboardType = sportEventData =>
  sportEventData && sportEventData.scoreboardType && sportEventData.scoreboardType.includes(SCOREBOARD_TYPE_AUTOMATIC);

const getTeamLogo = (number, assetId, assetBase64) => {
  if (assetId) {
    return { [`teamLogo${number}Id`]: assetId };
  }
  if (assetBase64) {
    return { [`teamLogo${number}Base64`]: assetBase64 && assetBase64.image ? assetBase64.image.split(",")[1] : {} };
  }
  return {};
};

const getScoreboardPreview = ({ eventData, contractId, token }) => {
  if (!eventData.scoreboardTemplateId) {
    return Promise.resolve("");
  }

  const body = {
    templateId: eventData.scoreboardTemplateId,
    teamName1: eventData.teamName1 || "",
    teamName2: eventData.teamName2 || "",
    teamName1Abreviated: eventData.teamName1Abreviated || "",
    teamName2Abreviated: eventData.teamName2Abreviated || "",
    ...getTeamLogo(1, eventData.teamLogo1, eventData.teamLogoAsset1),
    ...getTeamLogo(2, eventData.teamLogo2, eventData.teamLogoAsset2),
  };

  return fetch(getScoreboardFormPreviewUrl({ token, contractId }), {
    method: "POST",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
      authorization: `Bearer ${token}`,
    },
    mode: "cors",
  })
    .then(response => {
      if (!response.ok) {
        throw new Error("Error fetching scoreboard preview", response);
      }
      return response.blob();
    })
    .then(
      imageData =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(imageData);
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = error => reject(error);
        }),
    );
};

const SportApiForm = ({
  eventToEdit = {},
  classes,
  intl,
  values,
  handleChange,
  eventCustomerId,
  register,
  errors,
  showSportDataConfig,
  setShowSportDataConfig,
  watch,
  setValue,
}) => {
  const { token } = useAuthDataContext();

  const [open, setOpen] = useState(false);
  const [hasOnlyAutomaticBurnt, setHasOnlyAutomaticBurnt] = useState(false);
  const [hasOnlyNoScoreboard, setHasOnlyNoScoreboard] = useState(false);
  const [openSelectorModal, setOpenSelectorModal] = useState(false);
  const { setLoading } = useLoadingContext();
  const [scoreboardIdSelected, setScoreboardIdSelected] = useState(() =>
    values.sportEventData ? values.sportEventData.scoreboardTemplateId : null,
  );

  const [scoreboardNameSelected, setScoreboardNameSelected] = useState("");
  const [scoreboardPreviewBase64, setScoreboardPreviewBase64] = useState("");

  const handleScoreboardTypeChange = scoreboardType => {
    if (scoreboardType === SCOREBOARD_TYPE_NO_SCOREBOARD) {
      const currentTeamLogo1 = values.sportEventData?.teamLogo1 || "";
      const currentTeamLogo2 = values.sportEventData?.teamLogo2 || "";

      setValue("sportEventData", {
        teamLogo1: currentTeamLogo1,
        teamLogo2: currentTeamLogo2,
        scoreboardType: SCOREBOARD_TYPE_NO_SCOREBOARD,
      });
      return;
    }

    setValue("sportEventData.scoreboardType", scoreboardType);
  };

  const scoreboardType = values.sportEventData?.scoreboardType;

  useEffect(
    () => {
      if (values.sportEventData && scoreboardType) {
        setShowSportDataConfig(
          isManualScoreboardType(values.sportEventData) ||
            (isAutomaticScoreboardType(values.sportEventData) && !hasOnlyAutomaticBurnt),
        );
      }
    },
    // react-hook-form muta "sportEventData" in-place en los setValue anidados, así que su
    // referencia no cambia entre renders: hay que depender del primitivo scoreboardType, no
    // del objeto, para que este efecto reaccione al cambiar el tipo de marcador.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasOnlyAutomaticBurnt, setShowSportDataConfig, scoreboardType],
  );

  const handleOpen = () => {
    setLoading(true);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const contractId = values.service_contract_id || eventToEdit.service_contract_id;
  const eventTypeId = values.event_type_id || eventToEdit.event_type_id;
  const roomTemplateId = values.room_template_id || eventToEdit.room_template_id;

  useEffect(
    () => {
      const eventData = values.sportEventData;
      if (!eventData || !eventData.scoreboardTemplateId) {
        setScoreboardPreviewBase64("");
        return;
      }

      if (eventData && eventData.scoreboardTemplateId) {
        getScoreboardPreview({
          eventData: { ...values.sportEventData, scoreboardTemplateId: scoreboardIdSelected },
          contractId,
          token,
        })
          .then(preview => setScoreboardPreviewBase64(preview))
          .catch(error => {
            setScoreboardPreviewBase64("");
            console.error(error);
          });
      }
    },
    [token, scoreboardIdSelected, values.sportEventData.scoreboardType],
  );

  return values ? (
    <div style={{ width: "100%" }}>
      <PageHeaderBar subtitle title="app.page.event.form.sportData" />
      <PageSection hideBackground>
        <DataGrid>
          <Card>
            {!(contractId && eventTypeId && roomTemplateId) && <FormattedMessage id="event.scoreboardType.noData" />}
            {contractId && eventTypeId && roomTemplateId && !hasOnlyNoScoreboard && (
              <DataGridItem big>
                <ScoreboardTypeApiSelect
                  register={register}
                  label={intl.formatMessage({ id: "event.scoreboardType" })}
                  contractId={contractId}
                  eventTypeId={eventTypeId}
                  roomTemplateId={roomTemplateId}
                  classes={classes}
                  selectedElement={values?.sportEventData?.scoreboardType ?? ""}
                  setHasOnlyNoScoreboard={setHasOnlyNoScoreboard}
                  setHasOnlyAutomaticBurnt={setHasOnlyAutomaticBurnt}
                  onChange={handleScoreboardTypeChange}
                />
              </DataGridItem>
            )}
            {isManualScoreboardType(values.sportEventData) && (
              <DataGridItem big>
                <FormattedMessage id="event.scoreboardType.manual.info" />
              </DataGridItem>
            )}
            {isAutomaticScoreboardType(values.sportEventData) && hasOnlyAutomaticBurnt && (
              <DataGridItem big>
                <FormattedMessage id="event.scoreboardType.automaticrsi.only.info" />
              </DataGridItem>
            )}
            {showSportDataConfig && (
              <>
                <DataGridItem big className={classes.searchBox}>
                  <DataGrid>
                    <DataGridItem big className={classes.scoreboardTemplateSelector}>
                      <div className={classes.selectedScoreboardTemplateSelector}>
                        <TextDisplay
                          className={classes.data}
                          label={intl.formatMessage({ id: "event.selectedScoreboardTemplate" })}
                        >
                          {scoreboardNameSelected}
                        </TextDisplay>
                        <img
                          className={classes.preview_image}
                          src={getScoreboardTemplateThumbnail({
                            contractId,
                            scoreboardTemplateId: values.sportEventData?.scoreboardTemplateId,
                            token,
                          })}
                          alt={values.sportEventData?.scoreboardTemplateId}
                        />
                      </div>
                      <Button onClick={() => setOpenSelectorModal(true)} variant="contained" color="primary">
                        {intl.formatMessage({ id: "event.scoreboardTemplate" })}
                      </Button>
                    </DataGridItem>
                    {scoreboardPreviewBase64 && (
                      <DataGridItem big>
                        <PageHeaderBar subtitle title="event.sportEventData.sportData.scoreboard.preview" />
                        <div className={classes.scoreboardPreview_container}>
                          <img
                            onClick={handleOpen}
                            src={scoreboardPreviewBase64}
                            id="scoreboardPreview"
                            alt="Scoreboard Preview"
                            className={classes.scoreboardPreview}
                          />
                          <Dialog showCloseButton open={open} onClose={handleClose}>
                            <img
                              alt="Scoreboard Preview"
                              className={classes.scoreboardPreview_image}
                              onLoad={() => setLoading(false)}
                              src={scoreboardPreviewBase64}
                            />
                          </Dialog>
                        </div>
                      </DataGridItem>
                    )}
                  </DataGrid>
                  <ActionWithModalControlled
                    open={openSelectorModal}
                    setOpen={setOpenSelectorModal}
                    onOpen={() =>
                      setScoreboardIdSelected(values.sportEventData ? values.sportEventData.scoreboardTemplateId : null)
                    }
                    onClose={() => setScoreboardIdSelected(null)}
                    onAccept={() => handleChange("sportEventData.scoreboardTemplateId", scoreboardIdSelected || null)}
                    dialogTitle={intl.formatMessage({ id: "event.scoreboardTemplate" })}
                    renderDialogContent={() => (
                      <DataGrid>
                        <DataGridItem big>
                          <ScoreboardTemplatesApiTable
                            contractId={contractId}
                            eventTypeId={eventTypeId}
                            scoreboardIdSelected={scoreboardIdSelected}
                            setScoreboardIdSelected={setScoreboardIdSelected}
                            setScoreboardNameSelected={setScoreboardNameSelected}
                          />
                        </DataGridItem>
                      </DataGrid>
                    )}
                  />
                </DataGridItem>
              </>
            )}
          </Card>
        </DataGrid>
        {showSportDataConfig && (
          <>
            <DataGrid>
              <Card>
                <DataGridItem>
                  <TextField
                    {...register("sportEventData.teamName1Abreviated")}
                    id="sportEventData.teamName1Abreviated"
                    type="text"
                    placeholder={intl.formatMessage({ id: "event.sportEventData.teamName1Abreviated" })}
                    label={intl.formatMessage({ id: "event.sportEventData.teamName1Abreviated" })}
                    error={!!errors.sportEventData?.teamName1Abreviated}
                    helperText={errors.sportEventData?.teamName1Abreviated?.message}
                    className={classes.fullWidth}
                    maxLength={4}
                  />
                </DataGridItem>
                <DataGridItem />
                <DataGridItem>
                  <TextField
                    {...register("sportEventData.teamName1")}
                    id="sportEventData.teamName1"
                    type="text"
                    placeholder={intl.formatMessage({ id: "event.sportEventData.teamName1" })}
                    label={intl.formatMessage({ id: "event.sportEventData.teamName1" })}
                    error={!!errors.sportEventData?.teamName1}
                    helperText={errors.sportEventData?.teamName1?.message}
                    className={classes.fullWidth}
                  />
                </DataGridItem>
                <DataGridItem>
                  <LogoAssetApiForm
                    team={TEAM_1}
                    customerIdSelected={eventCustomerId}
                    assetPath="sportEventData.teamLogoAsset1"
                    assetIdPath="sportEventData.teamLogo1"
                    handleChange={handleChange}
                    assetId={values.sportEventData.teamLogo1 ?? ""}
                    asset={values.sportEventData.teamLogoAsset1 ?? {}}
                    classes={classes}
                    register={register}
                    watch={watch}
                  />
                </DataGridItem>
              </Card>
            </DataGrid>
            <DataGrid>
              <Card>
                <DataGridItem>
                  <TextField
                    {...register("sportEventData.teamName2Abreviated")}
                    id="sportEventData.teamName2Abreviated"
                    type="text"
                    placeholder={intl.formatMessage({ id: "event.sportEventData.teamName2Abreviated" })}
                    label={intl.formatMessage({ id: "event.sportEventData.teamName2Abreviated" })}
                    error={!!errors.sportEventData?.teamName2Abreviated}
                    helperText={errors.sportEventData?.teamName2Abreviated?.message}
                    className={classes.fullWidth}
                    maxLength={4}
                  />
                </DataGridItem>
                <DataGridItem />
                <DataGridItem>
                  <TextField
                    {...register("sportEventData.teamName2")}
                    id="sportEventData.teamName2"
                    type="text"
                    placeholder={intl.formatMessage({ id: "event.sportEventData.teamName2" })}
                    label={intl.formatMessage({ id: "event.sportEventData.teamName2" })}
                    error={!!errors.sportEventData?.teamName2}
                    helperText={errors.sportEventData?.teamName2?.message}
                    className={classes.fullWidth}
                  />
                </DataGridItem>
                <DataGridItem>
                  <LogoAssetApiForm
                    team={TEAM_2}
                    customerIdSelected={eventCustomerId}
                    assetPath="sportEventData.teamLogoAsset2"
                    assetIdPath="sportEventData.teamLogo2"
                    handleChange={handleChange}
                    assetId={values.sportEventData.teamLogo2 ?? ""}
                    asset={values.sportEventData.teamLogoAsset2 ?? {}}
                    classes={classes}
                    register={register}
                    watch={watch}
                  />
                </DataGridItem>
              </Card>
            </DataGrid>
          </>
        )}
      </PageSection>
      <MatchPointConfigApiForm
        handleChange={handleChange}
        values={values}
        eventToEdit={eventToEdit}
        register={register}
        watch={watch}
        errors={errors}
        intl={intl}
      />
    </div>
  ) : (
    ""
  );
};

SportApiForm.propTypes = {
  /**
   * Otros datos del evento a editar que no se envían en el form
   */
  eventToEdit: PropTypes.object,
  /**
   * Indica si es un formulario nuevo
   */
  isNewForm: PropTypes.bool,
  /**
   * Customer id del contrato del evento
   */
  eventCustomerId: PropTypes.string,
  /**
   * Valores del formulario
   */
  values: PropTypes.object,
  /**
   * Callback que se dispara con el cambio del input
   */
  handleChange: PropTypes.func,
};

export default injectIntl(withStyles(styles)(SportApiForm));
